// COPIA DICHIARATA di src/lib/scopa/rest.ts @ e99b95115c26c5085335a527db6cb22b23fd2f81 (TMA#735, 2026-10-20)
// Il REST di un gioco del kit dal BFF di Limulo: le rotte `/v1/games/<dominio>/…` (una per riga di `limulo-app/rotte.js` del gioco; le tabelle dei giochi del kit
// sono IDENTICHE nelle sei rotte e nei corpi). Funzioni PURE, niente rete: le query e le mutation stanno nel servizio del gioco, che le inietta in `limuloApi`.
// Cosa cambia rispetto alla fonte, oltre al gioco che diventa parametro:
//   · le rotte sono una FUNZIONE del `dominio` (`restDi`), non una costante; il ticket sta in `ticket.ts` (`ticketRoute`);
//   · i `posti` sono un numero: l'elenco dei posti ammessi lo da il gioco (`tavoliMieiOf(risposta, postiAmmessi)`), dove prima era «2 o 4»;
//   · i nomi perdono il prefisso del gioco (`guastoOf`, `GuastoTavolo`, `idempotencyKey`, `tavoloIdValido`); le costanti dei posti e il loro tipo escono (sono del gioco);
//   · il resto (la busta, la lettura chiusa dell'elenco, i corpi, il codice d'invito, la mappa dei guasti, la regola sulla chiave di idempotenza) e IDENTICO.
//
// La forma e quella del core del game host: la busta del BFF e `{ ok, organizationId, data: { provider, data: <corpo> }, meta }` e si scende SEMPRE con
// `capabilityPayloadOf(unwrapData(risposta))`, anche per l'elenco (che sta in `<corpo>.tables`). I corpi:
//   tables/mine   GET   → { tables: [{ tableId, posto, posti, attivo, wsUrl }] }
//   tables        POST  { tipo, posti, idempotencyKey } → 201 { tableId, posto, posti, invito, wsUrl }
//   seats         POST  { invito, idempotencyKey } (tableId nel percorso) → 200 { posto, wsUrl }
//   rivincita     POST  { idempotencyKey } (il corpo il core lo IGNORA) → 200|201 { tableId, posto, posti, classificato: false, wsUrl }
//   mescolate/:n  GET   → { record }
// Il `wsUrl` di queste risposte si IGNORA: l'indirizzo del socket e quello del ticket.
// Il client NON manda `classificato`, ne `sub`, `seats`, `occupanti`, `actAs`, `clientId` (il resolver li RIFIUTA, `FIELD_NOT_ALLOWED`), ne un tavolo di `gruppo`.
import { capabilityPayloadOf, unwrapData } from '@/lib/limulo/model';
import { limuloFailure } from '@/lib/limulo/errors';
import { tavoloIdValido } from './ticket';

/** Le rotte REST del gioco `dominio` (il segmento di `/v1/games/<dominio>`). */
export const restDi = (dominio: string) => {
  const base = `/v1/games/${dominio}`;
  return {
    tavoliMiei: `${base}/tables/mine`,
    tavoli: `${base}/tables`,
    posti: (tableId: string) => `${base}/tables/${tableId.toLowerCase()}/seats`,
    rivincita: (tableId: string) => `${base}/tables/${tableId.toLowerCase()}/rivincita`,
    mescolata: (tableId: string, smazzata: number) => `${base}/tables/${tableId.toLowerCase()}/mescolate/${smazzata}`,
  } as const;
};
export type RotteRest = ReturnType<typeof restDi>;

/** I due tipi che la TMA crea. ⛔ `gruppo` non si manda. */
export type TipoTavolo = 'casual' | 'privato';

const asRecord = (v: unknown): Record<string, unknown> => (v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const testo = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);
/** Il corpo del game host dalla busta del BFF. */
export const corpoDi = (risposta: unknown): Record<string, unknown> => asRecord(capabilityPayloadOf(unwrapData<unknown>(risposta)));

// ── I miei tavoli ───────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
export interface TavoloMio {
  /** L'uuid del tavolo, MINUSCOLO: e l'`id` della rotta del tavolo. */
  tableId: string;
  /** I posti del tavolo, se sono fra quelli che il gioco ammette; altrimenti `null`. */
  posti: number | null;
  /** `true` = tutti hanno preso il posto almeno una volta (la partita e partita); `false` = si aspetta ancora qualcuno. */
  attivo: boolean;
}
export type TavoliMiei = { ok: true; tavoli: TavoloMio[] } | { ok: false };

/**
 * Lettura CHIUSA: `<corpo>.tables` e un array, e nient'altro. Senza di lui la lettura e `ok: false`, mai «nessun tavolo» (il vuoto si afferma solo davanti a
 * un array vuoto vero). Una voce che non si legge (senza uuid, o con `attivo` che non e un booleano) rende NULLA l'intera lettura: un pezzo di elenco, detto
 * come l'elenco, sarebbe un vuoto parziale affermato. `postiAmmessi` e l'elenco del gioco.
 */
export function tavoliMieiOf(risposta: unknown, postiAmmessi: readonly number[]): TavoliMiei {
  const lista = corpoDi(risposta).tables;
  if (!Array.isArray(lista)) return { ok: false };
  const tavoli: TavoloMio[] = [];
  for (const voce of lista) {
    const r = asRecord(voce);
    const id = typeof r.tableId === 'string' ? r.tableId.trim() : '';
    if (!tavoloIdValido(id) || typeof r.attivo !== 'boolean') return { ok: false };
    tavoli.push({ tableId: id.toLowerCase(), posti: typeof r.posti === 'number' && postiAmmessi.includes(r.posti) ? r.posti : null, attivo: r.attivo });
  }
  return { ok: true, tavoli };
}

// ── Le scritture ────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
/** La chiave di idempotenza di UN'azione dell'utente: scelta UNA volta per azione, RIUSATA identica se il tentativo si ripete (una nuova duplicherebbe il tavolo). */
export const idempotencyKey = (): string => `tma-${crypto.randomUUID()}`;

export const creaTavoloBody = (a: { tipo: TipoTavolo; posti: number; idempotencyKey: string }) => ({ tipo: a.tipo, posti: a.posti, idempotencyKey: a.idempotencyKey });
export const entraBody = (a: { invito: string; idempotencyKey: string }) => ({ invito: a.invito, idempotencyKey: a.idempotencyKey });
export const rivincitaBody = (a: { idempotencyKey: string }) => ({ idempotencyKey: a.idempotencyKey });

export interface TavoloCreato {
  tableId: string;
  /** Il codice d'invito di un tavolo PRIVATO, reso UNA volta a chi crea; `null` = assente (un casual, o un replay col tavolo pieno). */
  invito: string | null;
}
/** Il tavolo creato (o la rivincita): senza un `tableId` uuid e un `posto` intero e `null`, mai un `/<gioco>/tavolo/undefined`. */
export function tavoloCreatoOf(risposta: unknown): TavoloCreato | null {
  const r = corpoDi(risposta);
  const id = testo(r.tableId);
  if (id === null || !tavoloIdValido(id) || !Number.isInteger(r.posto)) return null;
  return { tableId: id.toLowerCase(), invito: testo(r.invito) };
}

/** L'ingresso riuscito: il `tableId` e quello del PERCORSO; senza un `posto` intero la risposta non si legge. */
export function ingressoOf(risposta: unknown, tableId: string): { tableId: string } | null {
  const r = corpoDi(risposta);
  if (!Number.isInteger(r.posto) || !tavoloIdValido(tableId)) return null;
  const rimandato = testo(r.tableId);
  if (rimandato !== null && rimandato.toLowerCase() !== tableId.toLowerCase()) return null;
  return { tableId: tableId.toLowerCase() };
}

/** Il record di una smazzata finita (`qlash-fair-v1`), cosi come arriva. `null` senza un oggetto `record`. */
export function mescolataOf(risposta: unknown): Record<string, unknown> | null {
  const r = corpoDi(risposta).record;
  return r !== null && typeof r === 'object' && !Array.isArray(r) ? (r as Record<string, unknown>) : null;
}

// ── Il CODICE da condividere (convenzione NOSTRA: `<tableId senza trattini>.<invito>`) ⚠️ e una credenziale: mai in log, storage, URL ──────
const INVITO = /^[A-Za-z0-9_-]{1,200}$/;
const ESA32 = /^[0-9a-fA-F]{32}$/;
export function codiceInvito(tableId: string, invito: string): string {
  return `${tableId.replace(/-/g, '').toLowerCase()}.${invito}`;
}
export function leggiCodiceInvito(testoIncollato: string): { tableId: string; invito: string } | null {
  const t = testoIncollato.trim();
  const i = t.indexOf('.');
  if (i <= 0) return null;
  const sinistra = t.slice(0, i);
  const invito = t.slice(i + 1);
  if (!INVITO.test(invito)) return null;
  const nudo = sinistra.replace(/-/g, '');
  if (!ESA32.test(nudo) || (sinistra.includes('-') && sinistra.length !== 36)) return null;
  const n = nudo.toLowerCase();
  const tableId = `${n.slice(0, 8)}-${n.slice(8, 12)}-${n.slice(12, 16)}-${n.slice(16, 20)}-${n.slice(20)}`;
  return tavoloIdValido(tableId) ? { tableId, invito } : null;
}

// ── I guasti ────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
/**
 * Perche una chiamata al BFF non e andata. Il codice sta in `code`; il BFF appiattisce ogni 4xx del game host in `E_QLASH_HTTP_<status>`, quindi si decide sullo STATUS.
 *  · `non-disponibile` — SOLO `424 E_QLASH_CAPABILITY_DISABLED` (il gioco e spento per l'organizzazione, o la rotta non e servita): uno stato, non un guasto;
 *  · `rete` — niente status HTTP; `conflitto` (409: gia seduto altrove, tavolo pieno, rivincita piena o con un robot); `vietato` (403); `nonTrovato` (404: codice
 *    sbagliato, tavolo finito o inesistente — UNA risposta, di proposito); `richiesta` (400/422); `troppeRichieste` (429); `server` (5xx); `errore` (ogni altro).
 * ⛔ Un 424 qualunque NON e «non disponibile»: il BFF fa uscire col 424 ogni guasto del provider (lezione di TMA#691).
 */
export type GuastoTavolo = 'non-disponibile' | 'rete' | 'conflitto' | 'vietato' | 'nonTrovato' | 'richiesta' | 'troppeRichieste' | 'server' | 'errore';

export function guastoOf(errore: unknown): GuastoTavolo {
  const f = limuloFailure(errore);
  if (f.status === 424 && f.code === 'E_QLASH_CAPABILITY_DISABLED') return 'non-disponibile';
  const s = f.status;
  if (s === null) return 'rete';
  if (s === 409) return 'conflitto';
  if (s === 403) return 'vietato';
  if (s === 404) return 'nonTrovato';
  if (s === 400 || s === 422) return 'richiesta';
  if (s === 429) return 'troppeRichieste';
  if (s >= 500) return 'server';
  return 'errore';
}

/**
 * La chiave di idempotenza si TIENE per il tentativo successivo solo se la risposta puo essersi persa o se il guasto e passeggero: rete, 5xx, 424 (la richiesta puo
 * essere arrivata). Un rifiuto DEFINITIVO (un 4xx che non e 424) la scarta.
 */
export function teniamoLaChiave(errore: unknown): boolean {
  const s = limuloFailure(errore).status;
  return s === null || s === 424 || s >= 500;
}
